import { datadogRum } from "@datadog/browser-rum";
import { useOktaAuth } from "@okta/okta-react";
import { createFileRoute, Navigate, Outlet, useLocation, useMatchRoute, useParams } from "@tanstack/react-router";
import { Spinner } from "@teamworksdev/react";
import { useEffect, useRef } from "react";
import MainNav from "~/components/MainNav";
import { isGovCloud } from "~/config";
import storage from "~/services/localStorage";

const Protected = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const { pathname } = useLocation();
  const datadogInitialized = useRef(false);
  const matchRoute = useMatchRoute();
  const isOrgsPath = !!matchRoute({ to: "/orgs" });
  const isOrgPath = !!matchRoute({ to: "/orgs/$orgId", fuzzy: true });

  // Initialize Datadog RUM user
  useEffect(() => {
    if (authState?.isAuthenticated) {
      if (!datadogInitialized.current) {
        datadogRum.setUser({
          email: authState.idToken?.claims?.email,
          name: authState.idToken?.claims?.name,
        });
        datadogInitialized.current = true;
      }
    }
  }, [authState]);

  const { orgId } = useParams({ strict: false });

  // Set the selected org document id in local storage
  useEffect(() => {
    if (!isOrgsPath && orgId) {
      storage.setSelectedOrgDocumentId(orgId);
    }
  }, [isOrgsPath, orgId]);

  if (!authState) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // If the user is not authenticated, redirect to the login page
  if (!authState.isAuthenticated) {
    oktaAuth.signInWithRedirect();
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (pathname === "/") {
    return <Navigate to="/orgs" />;
  }

  if (isOrgPath) {
    return <Outlet />;
  }

  return (
    <div className="relative flex min-h-screen flex-col">
      <header
        className={`supports-backdrop-blur:bg-white/60 sticky ${isGovCloud ? "top-10" : "top-0"} z-40 w-full border-b bg-white/95 backdrop-blur`}
      >
        <div className="container flex h-14 items-center">
          <MainNav />
        </div>
      </header>
      <div className="flex-1">
        <div className="container flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_protected")({
  component: Protected,
});
